import type {
  AloftPriceJSON,
  AppraisifyAllowedPropertyType,
  LatitudeLongitude,
  MainPropertyType,
} from '@aloft/toolkit-shared';
import {
  formatPropertyType,
  noop,
  schAloftPrice,
  schAppraisifyAllowedPropertyTypes,
  schHoaFeeFrequency,
  schPropertyInterest,
  schZoningCompliance,
} from '@aloft/toolkit-shared';
import { Block, Button, Checkbox, Flex, Input, Select, Typography } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { Divider } from '@aloft/toolkit-sui/chakra';

import { useEffect, useState } from 'react';
import { Controller, Form, FormProvider } from 'react-hook-form';
import type { SubmitErrorHandler, SubmitHandler } from 'react-hook-form';
import type { z } from 'zod';

import { FormTextarea } from '../../../components/FormTextarea/FormTextarea.tsx';
import {
  ALERT_TARGET_FIELD_TITLE,
  getAlertTargetInputFieldSx,
} from '../../../components/PropertyGrid/fields/styles.ts';
import { ESCALATION_TYPE_MESSAGES } from '../../../hooks/validation/consts.ts';
import { useAppraisifyValidation } from '../../../hooks/validation/useAppraisifyValidation.ts';
import { submitEditSubjectPropertyDetailsForm } from '../../../redux/features/actions/submitEditSubjectPropertyDetailsForm.ts';
import {
  selectPropertyIdentificationById,
  selectSubjectAddress,
} from '../../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectHoveredOrderDetailsFieldKey } from '../../../redux/features/selectors/referenceBarUISelectors.ts';
import { selectSubjectPropertyType } from '../../../redux/features/selectors/selectSubjectPropertyType.ts';
import { selectFocusedOrderDetailsFieldKey } from '../../../redux/features/selectors/uiSelectors.ts';
import {
  setFocusedOrderDetailsFieldKey,
  setStopAndCallManualIssue,
} from '../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import type { LocationType } from '../../../utils/locationTypeProximityRequirementMiles.ts';
import {
  formatMiles,
  getLocationTypeProximityRequirementMiles,
} from '../../../utils/locationTypeProximityRequirementMiles.ts';
import { getLocationTypeSaleDateRequirementMonths } from '../../../utils/locationTypeSaleDateRequirementMonths.ts';
import { OrderDetailsFieldAnchor } from '../components/OrderDetailsFieldAnchor.tsx';
import { OrderDetailsInput } from '../components/OrderDetailsInput.tsx';
import { SubjectPropertyLocationModal } from '../components/SubjectPropertyLocationModal.tsx';
import { useSubjectPropertyDetailsForm } from '../hooks/useSubjectPropertyDetailsForm.ts';
import type { SubjectPropertyDetailsFormOutput } from '../schemas.ts';
import { isOrderDetailsFieldAlertHighlighted } from '../useOrderDetailsFieldAlertHighlight.ts';

const locationTypeOptions: { label: string; value: LocationType }[] = (
  ['Urban', 'Suburban', 'Rural'] as const
).map((value) => ({
  value,
  label: `${value} (${getLocationTypeProximityRequirementMiles(value)!} mi; last ${getLocationTypeSaleDateRequirementMonths(value)!} mo)`,
}));

const propertyTypeOptions: { label: string; value: AppraisifyAllowedPropertyType }[] = (
  schAppraisifyAllowedPropertyTypes.options as AppraisifyAllowedPropertyType[]
).map((value) => ({
  value,
  label: formatPropertyType(value) ?? '',
}));

// Component that calls validation hook from within form context
export const SubjectPropertyDetails = () => {
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const validation = useAppraisifyValidation();
  const {
    zoning,
    zoningCompliance,
    parcelNumber,
    legalDescription,
    locationType: locationTypeError,
    propertyInterest,
    propertyInterestOther,
    hoa: neighborhoodName,
  } = validation.errors.OrderDetails;

  const subjectPropertyIdentification = useAppSelector((state) =>
    selectPropertyIdentificationById(state, 'subject'),
  );
  const subjectAddress = useAppSelector(selectSubjectAddress);
  const propertyType = useAppSelector((state) => selectSubjectPropertyType(state));
  const form = useSubjectPropertyDetailsForm();
  const dispatch = useAppDispatch();
  const focusedOrderDetailsFieldKey = useAppSelector(selectFocusedOrderDetailsFieldKey);
  const hoveredOrderDetailsFieldKey = useAppSelector(selectHoveredOrderDetailsFieldKey);
  const isOrderDetailsFieldFocusedFromAlert = (fieldName: string) =>
    isOrderDetailsFieldAlertHighlighted(
      fieldName,
      focusedOrderDetailsFieldKey,
      hoveredOrderDetailsFieldKey,
    );
  const orderDetailsAlertFieldSx = (fieldName: string) =>
    getAlertTargetInputFieldSx(isOrderDetailsFieldFocusedFromAlert(fieldName), 'error');
  const clearOrderDetailsFieldFocus = () => dispatch(setFocusedOrderDetailsFieldKey(undefined));
  const pud = form.watch('pud') ?? false;
  const locationType = form.watch('locationType') as LocationType | undefined;
  const proximityRequirementMiles = getLocationTypeProximityRequirementMiles(locationType);
  const saleDateRequirementMonths = getLocationTypeSaleDateRequirementMonths(locationType);
  const propertyInterestValue = form.watch('propertyInterest');
  const hoaFeeFrequencyError = form.formState.errors.hoaFeeFrequency?.message;

  useEffect(() => {
    form.reset({
      zoning: subjectPropertyIdentification?.zoning ?? '',
      zoningCompliance: subjectPropertyIdentification?.zoningCompliance ?? undefined,
      legalDescription: subjectPropertyIdentification?.legalDescription ?? '',
      locationType: subjectPropertyIdentification?.locationType ?? undefined,
      hoa: subjectPropertyIdentification?.hoa ?? '',
      hoaFeeAmount: subjectPropertyIdentification?.hoaFeeAmount
        ? schAloftPrice.parse(subjectPropertyIdentification?.hoaFeeAmount)
        : undefined,
      hoaFeeFrequency: subjectPropertyIdentification?.hoaFeeFrequency,
      parcelNumber: subjectPropertyIdentification?.parcelNumber ?? '',
      pud: subjectPropertyIdentification?.pud ?? false,
      propertyInterest: subjectPropertyIdentification?.propertyInterest ?? undefined,
      propertyInterestOther: subjectPropertyIdentification?.propertyInterestOther ?? '',
      coordinates: subjectPropertyIdentification?.coordinates,
      propertyType,
    });
  }, [subjectPropertyIdentification, propertyType]);

  const onSubmit: SubmitHandler<SubjectPropertyDetailsFormOutput> = (data) => {
    dispatch(submitEditSubjectPropertyDetailsForm(data));
  };

  const onError: SubmitErrorHandler<
    Omit<SubjectPropertyDetailsFormOutput, 'hoaFeeAmount'> & { hoaFeeAmount?: AloftPriceJSON }
  > = console.error;

  const handleChange = () => {
    form.handleSubmit(onSubmit, onError)().catch(noop);
  };

  const handleNeighborhoodNameChange = (value: string) => {
    const currentValues = form.getValues();
    dispatch(
      submitEditSubjectPropertyDetailsForm({
        ...currentValues,
        hoa: value,
      } as SubjectPropertyDetailsFormOutput),
    );
  };

  const handleLocationTypeChange = (value?: LocationType) => {
    form.setValue('locationType', value, { shouldDirty: true, shouldValidate: true });
    const currentValues = form.getValues();
    dispatch(
      submitEditSubjectPropertyDetailsForm({
        ...currentValues,
        locationType: value,
      } as SubjectPropertyDetailsFormOutput),
    );
  };

  const handlePropertyTypeChange = (value?: MainPropertyType) => {
    form.setValue('propertyType', value, { shouldDirty: true, shouldValidate: true });
    const currentValues = form.getValues();
    dispatch(
      submitEditSubjectPropertyDetailsForm({
        ...currentValues,
        propertyType: value,
      } as SubjectPropertyDetailsFormOutput),
    );
  };

  // Save propertyInterest immediately when it changes, even if validation fails
  const handlePropertyInterestChange = (value?: string) => {
    if (!value) return;

    // Get current form values
    const currentValues = form.getValues();
    // Save propertyInterest immediately, even if propertyInterestOther is invalid
    dispatch(
      submitEditSubjectPropertyDetailsForm({
        ...currentValues,
        propertyInterest: value as z.infer<typeof schPropertyInterest>,
        // Clear propertyInterestOther if not "Other"
        propertyInterestOther: value !== 'Other' ? '' : currentValues.propertyInterestOther,
      } as SubjectPropertyDetailsFormOutput),
    );
  };

  // Save propertyInterestOther when it changes and is valid
  const handlePropertyInterestOtherChange = (value: string) => {
    const currentValues = form.getValues();
    // Only save if propertyInterest is "Other" and the value is not empty
    if (currentValues.propertyInterest === 'Other' && value.trim().length > 0) {
      dispatch(
        submitEditSubjectPropertyDetailsForm({
          ...currentValues,
          propertyInterestOther: value,
        } as SubjectPropertyDetailsFormOutput),
      );
    }
  };

  const handlePudChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    form.setValue('pud', e.target.checked);
  };

  const handleCannotDetermineAPN = () => {
    dispatch(
      setStopAndCallManualIssue({
        title: "You have indicated that the Subject's parcel number (APN) cannot be determined.",
        description: ESCALATION_TYPE_MESSAGES.parcel_number_unavailable,
        fieldName: 'parcelNumber',
        apiEscalationType: 'parcel_number_unavailable',
      }),
    );
  };

  const handleLocationModalClose = (newCoordinates?: LatitudeLongitude) => {
    setIsLocationModalOpen(false);
    if (!newCoordinates) {
      return;
    }
    form.setValue('coordinates', newCoordinates, { shouldDirty: true, shouldValidate: true });
    const currentValues = form.getValues();
    dispatch(
      submitEditSubjectPropertyDetailsForm({
        ...currentValues,
        coordinates: newCoordinates,
      } as SubjectPropertyDetailsFormOutput),
    );
  };

  return (
    <>
      <SubjectPropertyLocationModal
        isOpen={isLocationModalOpen}
        onClose={handleLocationModalClose}
        currentCoordinates={subjectPropertyIdentification?.coordinates}
      />
      <FormProvider {...form}>
        <Form onChange={handleChange}>
          <Flex flexDirection="column" padding="15px">
            <Typography variant="h2">Subject Property Details</Typography>
            <Divider
              borderColor="strokeDefault"
              marginY="10px"
              borderTopWidth="1px"
              borderBottomWidth="0px"
            />
            <Flex gap="15px" lineHeight="32px" flexDirection="column">
              <Flex>
                <Block width="250px">
                  <Typography variant="h4" color="textSecondary">
                    Subject Property Address
                  </Typography>
                </Block>
                <Typography variant="body1" color="textPrimary">
                  {subjectAddress}
                </Typography>
              </Flex>
              <Flex>
                <Block width="250px">
                  <Typography variant="h4" color="textSecondary">
                    Property Type
                  </Typography>
                </Block>
                <Block width="250px">
                  <Select
                    options={propertyTypeOptions}
                    size="sm"
                    value={form.watch('propertyType') ?? undefined}
                    onChange={(value) => handlePropertyTypeChange(value || undefined)}
                  />
                </Block>
              </Flex>
              <OrderDetailsInput
                control={form.control}
                name="zoning"
                label="Zoning"
                isRequired
                placeholderText="Zoning type"
                errorMessage={zoning?.message}
              />
              <Flex flexDirection="column" gap="4px">
                <Flex>
                  <Block width="250px">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="h4" color="textSecondary">
                        Location Type
                      </Typography>
                      <span style={{ color: 'red' }}> *</span>
                    </div>
                  </Block>
                  <OrderDetailsFieldAnchor fieldName="locationType">
                    <Block width="250px">
                      <Select
                        options={locationTypeOptions}
                        placeholder=" "
                        size="sm"
                        value={form.watch('locationType') ?? ''}
                        isInvalid={!!locationTypeError}
                        title={
                          isOrderDetailsFieldFocusedFromAlert('locationType')
                            ? ALERT_TARGET_FIELD_TITLE
                            : undefined
                        }
                        __dangerDoNotUseSx={orderDetailsAlertFieldSx('locationType')}
                        onChange={(value) => {
                          clearOrderDetailsFieldFocus();
                          handleLocationTypeChange(
                            (value || undefined) as LocationType | undefined,
                          );
                        }}
                      />
                      {locationTypeError && (
                        <Typography variant="body2" color="textError">
                          {locationTypeError.message}
                        </Typography>
                      )}
                    </Block>
                  </OrderDetailsFieldAnchor>
                </Flex>
                {proximityRequirementMiles !== undefined &&
                  saleDateRequirementMonths !== undefined && (
                    <Flex marginLeft="250px">
                      <Typography variant="body2" color="textSecondary">
                        Note: use Comps within {formatMiles(proximityRequirementMiles)} of the
                        Subject, contracted within the last {saleDateRequirementMonths} months.
                      </Typography>
                    </Flex>
                  )}
              </Flex>
              <Flex>
                <Block width="250px">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h4" color="textSecondary">
                      Zoning Compliance
                    </Typography>
                    <span style={{ color: 'red' }}> *</span>
                  </div>
                </Block>
                <OrderDetailsFieldAnchor fieldName="zoningCompliance">
                  <Block width="250px">
                    <Select
                      options={schZoningCompliance.options.map((option) => ({
                        label: option,
                        value: option,
                      }))}
                      placeholder=" "
                      size="sm"
                      value={form.watch('zoningCompliance') || ''}
                      onChange={(value) => {
                        clearOrderDetailsFieldFocus();
                        form.setValue('zoningCompliance', value);
                      }}
                      isInvalid={!!zoningCompliance}
                      title={
                        isOrderDetailsFieldFocusedFromAlert('zoningCompliance')
                          ? ALERT_TARGET_FIELD_TITLE
                          : undefined
                      }
                      __dangerDoNotUseSx={orderDetailsAlertFieldSx('zoningCompliance')}
                    />
                    {zoningCompliance && (
                      <Typography variant="body2" color="textError">
                        {zoningCompliance.message}
                      </Typography>
                    )}
                  </Block>
                </OrderDetailsFieldAnchor>
              </Flex>
              <Flex alignItems="flex-start" flexWrap="nowrap" gap="10px">
                <OrderDetailsInput
                  control={form.control}
                  name="hoa"
                  label="Neighborhood Name"
                  placeholderText="Neighborhood name"
                  isRequired
                  errorMessage={neighborhoodName?.message}
                  onValueChange={(value) =>
                    handleNeighborhoodNameChange(typeof value === 'string' ? value : '')
                  }
                />
                <Block>
                  <Checkbox isChecked={pud} onChange={handlePudChange}>
                    <Typography variant="body1" color="textPrimary">
                      PUD
                    </Typography>
                  </Checkbox>
                </Block>
              </Flex>
              <Flex>
                <OrderDetailsInput
                  control={form.control}
                  name="hoaFeeAmount"
                  label="Mandatory Fees"
                  placeholderText="Dollar amount of the mandatory fees"
                  type="aloftPrice"
                />
                <Block marginLeft="10px" marginTop="-1px">
                  <Block width="250px">
                    <Select
                      options={[
                        { label: '', value: '' },
                        ...schHoaFeeFrequency.options.map((option) => ({
                          label: option,
                          value: option,
                        })),
                      ]}
                      size="sm"
                      value={form.watch('hoaFeeFrequency') ?? ''}
                      onChange={(value) =>
                        form.setValue('hoaFeeFrequency', value === '' ? undefined : value)
                      }
                      isInvalid={!!hoaFeeFrequencyError}
                    />
                    {!!hoaFeeFrequencyError && (
                      <Typography variant="body2" color="textError">
                        {hoaFeeFrequencyError}
                      </Typography>
                    )}
                  </Block>
                </Block>
              </Flex>
              <Flex alignItems="flex-start" flexWrap="nowrap" gap="10px">
                <OrderDetailsInput
                  control={form.control}
                  name="parcelNumber"
                  label="Parcel Number"
                  isRequired
                  placeholderText="Parcel number of the property"
                  errorMessage={parcelNumber?.message}
                />
                <Block>
                  <Button
                    text="Cannot determine"
                    onClick={handleCannotDetermineAPN}
                    variant="secondary"
                    size="sm"
                  />
                </Block>
              </Flex>

              <Flex alignItems="flex-start" flexWrap="nowrap" marginTop="-10px">
                <Block width="250px" />
                <Button
                  text="Verify Subject location"
                  onClick={() => {
                    setIsLocationModalOpen(true);
                  }}
                  variant="inline"
                  icon="location-crosshairs"
                  size="sm"
                />
              </Flex>
              <Flex>
                <Block width="250px">
                  <Flex alignItems="center">
                    <Typography variant="h4" color="textSecondary">
                      Property Interest
                    </Typography>
                    <span style={{ color: 'red' }}> *</span>
                  </Flex>
                </Block>
                <OrderDetailsFieldAnchor fieldName="propertyInterest">
                  <Block width="250px">
                    <Controller
                      name="propertyInterest"
                      control={form.control}
                      rules={{
                        required: true,
                      }}
                      render={({ field, fieldState }) => (
                        <>
                          <Select
                            options={schPropertyInterest.options.map((option) => ({
                              label: option,
                              value: option,
                            }))}
                            placeholder=" "
                            size="sm"
                            value={field.value ?? ''}
                            onChange={(value) => {
                              clearOrderDetailsFieldFocus();
                              field.onChange(value);
                              if (value !== 'Other') {
                                form.setValue('propertyInterestOther', '');
                              }
                              handlePropertyInterestChange(value);
                              form.trigger('propertyInterestOther').catch(noop);
                            }}
                            isInvalid={!!propertyInterest || fieldState.invalid}
                            title={
                              isOrderDetailsFieldFocusedFromAlert('propertyInterest')
                                ? ALERT_TARGET_FIELD_TITLE
                                : undefined
                            }
                            __dangerDoNotUseSx={orderDetailsAlertFieldSx('propertyInterest')}
                          />
                          {(propertyInterest || fieldState.error) && (
                            <Typography variant="body2" color="textError">
                              {propertyInterest?.message || fieldState.error?.message || 'Required'}
                            </Typography>
                          )}
                        </>
                      )}
                    />
                  </Block>
                </OrderDetailsFieldAnchor>
              </Flex>
              {propertyInterestValue === 'Other' && (
                <Controller
                  name="propertyInterestOther"
                  control={form.control}
                  rules={{
                    required: propertyInterestValue === 'Other' ? 'Required' : false,
                  }}
                  render={({ field, fieldState }) => {
                    const formError = fieldState.error?.message;
                    const customError = propertyInterestOther?.message;
                    const hasError = formError || customError;

                    return (
                      <OrderDetailsFieldAnchor fieldName="propertyInterestOther">
                        <Flex>
                          <Block width="250px">
                            <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                              <Typography variant="h4" color="textSecondary">
                                Property Interest (Other)
                              </Typography>
                              <span style={{ color: 'red' }}> *</span>
                            </div>
                          </Block>
                          <Block width="250px">
                            <Input
                              size="sm"
                              type="text"
                              placeholderText="Specify property interest"
                              isInvalid={!!hasError}
                              title={
                                isOrderDetailsFieldFocusedFromAlert('propertyInterestOther')
                                  ? ALERT_TARGET_FIELD_TITLE
                                  : undefined
                              }
                              __dangerDoNotUseSx={orderDetailsAlertFieldSx('propertyInterestOther')}
                              {...field}
                              onFocus={() => {
                                if (isOrderDetailsFieldFocusedFromAlert('propertyInterestOther')) {
                                  clearOrderDetailsFieldFocus();
                                }
                              }}
                              onChange={(value) => {
                                field.onChange(value);
                                handlePropertyInterestOtherChange(value);
                              }}
                              onClear={() => {
                                field.onChange('');
                                handlePropertyInterestOtherChange('');
                              }}
                              isRequired
                            />
                            {!!hasError && (
                              <Typography variant="body2" color="textError">
                                {formError || customError}
                              </Typography>
                            )}
                          </Block>
                        </Flex>
                      </OrderDetailsFieldAnchor>
                    );
                  }}
                />
              )}
              <OrderDetailsFieldAnchor fieldName="legalDescription">
                <FormTextarea
                  name="legalDescription"
                  control={form.control}
                  label="Legal Description"
                  isRequired
                  placeholder="Legal Description of the property"
                  customErrorMessage={legalDescription?.message}
                />
              </OrderDetailsFieldAnchor>
            </Flex>
          </Flex>
        </Form>
      </FormProvider>
    </>
  );
};
